import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Construction,
  CheckCircle2,
  Clock,
  Lightbulb,
  ChevronRight,
} from "lucide-react";

export default function ComingSoonPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const titleFromPath = (p: string) => {
    const parts = p.split("/").filter(Boolean);
    if (!parts.length) return "Coming Soon";
    const head = parts[0];
    const rest = parts
      .slice(1)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" / ");
    const nice = head.charAt(0).toUpperCase() + head.slice(1);
    return rest ? `${nice} — ${rest}` : nice;
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-8 animate-in fade-in duration-500">
      {/* TOP NAVIGATION / BACK BUTTON */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)} // Takes user back to previous page
          className="group flex items-center gap-2 px-4 py-2 bg-white border border-gray-100 rounded-2xl text-sm font-bold text-gray-600 hover:text-indigo-600 hover:border-indigo-100 hover:shadow-sm transition-all"
        >
          <ArrowLeft
            size={18}
            className="group-hover:-translate-x-1 transition-transform"
          />
          Back
        </button>

        <div className="hidden md:flex items-center gap-2 bg-indigo-50 px-4 py-2 rounded-2xl">
          <Construction size={16} className="text-indigo-600" />
          <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700">
            Work in Progress
          </span>
        </div>
      </div>

      {/* MAIN HERO SECTION */}
      <section className="bg-white border border-gray-100 rounded-[2.5rem] p-8 md:p-12 shadow-sm relative overflow-hidden">
        {/* Decorative Background Element */}
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-64 h-64 bg-indigo-50 rounded-full blur-3xl opacity-50" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight leading-tight">
              {titleFromPath(path)}
            </h1>
            <p className="text-lg text-gray-500 mt-4 font-medium leading-relaxed">
              We're currently fine-tuning this section to ensure the best
              experience. Please check back soon for the full release.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-100 rounded-xl text-xs font-mono text-gray-400">
              <span className="font-bold text-gray-500 uppercase tracking-tighter mr-2">
                Route:
              </span>
              {path}
            </div>
          </div>

          <button
            onClick={() => navigate("/dashboard")}
            className="shrink-0 px-8 py-4 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-600 hover:shadow-lg hover:shadow-indigo-100 transition-all active:scale-95"
          >
            Go to Dashboard
          </button>
        </div>
      </section>

      {/* INFORMATION GRID */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Available Now Card */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 hover:shadow-md transition-shadow">
          <div className="w-10 h-10 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center mb-4">
            <CheckCircle2 size={20} />
          </div>
          <h3 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-4 italic">
            Available now
          </h3>
          <ul className="space-y-3">
            {["Dashboard", "Logbook Registry", "Demand Notes and Payments"].map(
              (item) => (
                <li
                  key={item}
                  className="flex items-center gap-2 text-sm font-bold text-gray-700"
                >
                  <ChevronRight size={14} className="text-indigo-500" />
                  {item}
                </li>
              )
            )}
          </ul>
        </div>

        {/* Coming Soon Card */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 hover:shadow-md transition-shadow">
          <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-4">
            <Clock size={20} />
          </div>
          <h3 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-4 italic">
            Up Next
          </h3>
          <ul className="space-y-3">
            {[
              "Membership Upgrades",
              "Advanced Training",
              "Chapter Management",
              "Finance Analytics",
            ].map((item) => (
              <li
                key={item}
                className="flex items-center gap-2 text-sm font-bold text-gray-600 opacity-60"
              >
                <div className="w-1 h-1 rounded-full bg-gray-300" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Developer Tip Card */}
        <div className="bg-indigo-600 text-white rounded-3xl p-6 shadow-lg shadow-indigo-100 relative overflow-hidden">
          <Lightbulb className="absolute bottom-[-10px] right-[-10px] w-24 h-24 text-white/10 rotate-12" />
          <div className="w-10 h-10 bg-white/20 text-white rounded-2xl flex items-center justify-center mb-4">
            <Lightbulb size={20} />
          </div>
          <h3 className="text-sm font-black uppercase tracking-widest text-white/60 mb-3 italic">
            Developer Note
          </h3>
          <p className="text-sm leading-relaxed font-medium text-indigo-50">
            Once development for{" "}
            <span className="underline underline-offset-4">{path}</span> is
            complete, it will be made available on the platform{" "}
            <code className="bg-white/10 px-1 rounded text-xs font-mono">
              ComingSoon
            </code>{" "}
          </p>
        </div>
      </div>
    </div>
  );
}
